import React, { useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useMutation, useQuery } from '@tanstack/react-query'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface Employee {
  id: number
  employeeNumber: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  department: string
  position: string
  employmentStatus: 'active' | 'inactive' | 'terminated'
  employmentType: 'full_time' | 'part_time' | 'contract'
  baseSalary: string
  salaryCurrency: 'USD' | 'CDF'
  hireDate: string
  bankName?: string | null
  bankAccount?: string | null
  manager?: {
    id: number
    firstName: string
    lastName: string
    employeeNumber: string
  }
  compensation?: {
    id: number
    compensationType: string
    name: string
    amount: string
    currency: string
    frequency: string
  }[]
}

interface EditEmployeeModalProps {
  employee: Employee
  onClose: () => void
  onSuccess: () => void
}

interface Manager {
  id: number
  firstName: string
  lastName: string
  employeeNumber: string
}

const EditEmployeeModal: React.FC<EditEmployeeModalProps> = ({ employee, onClose, onSuccess }) => {
  // Extract current allowances from compensation array
  const transportAllowance = employee.compensation?.find(c => c.name === 'Transport Allowance')?.amount || '0'
  const telephoneAllowance = employee.compensation?.find(c => c.name === 'Telephone Allowance')?.amount || '0'
  const dataAllowance = employee.compensation?.find(c => c.name === 'Data Allowance')?.amount || '0'

  const [formData, setFormData] = useState({
    firstName: employee.firstName,
    lastName: employee.lastName,
    email: employee.email,
    phone: employee.phone || '',
    department: employee.department,
    position: employee.position,
    employmentType: employee.employmentType,
    employmentStatus: employee.employmentStatus,
    baseSalary: employee.baseSalary,
    salaryCurrency: employee.salaryCurrency,
    hireDate: employee.hireDate.split('T')[0], // Convert to YYYY-MM-DD format
    managerId: employee.manager?.id || undefined,
    bankName: employee.bankName || '',
    bankAccount: employee.bankAccount || '',
    transportAllowance: transportAllowance,
    telephoneAllowance: telephoneAllowance,
    dataAllowance: dataAllowance,
  })

  // Fetch potential managers (excluding current employee)
  const { data: managers } = useQuery<{ employees: Manager[] }>({
    queryKey: ['potential-managers', employee.id],
    queryFn: async () => {
      const response = await api.get('/employees?limit=100')
      return {
        employees: response.data.data.employees.filter((emp: Manager) => emp.id !== employee.id)
      }
    }
  })

  const updateEmployeeMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.put(`/employees/${employee.id}`, data)
      return response.data
    },
    onSuccess: () => {
      toast.success('Employee updated successfully')
      onSuccess()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update employee')
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateEmployeeMutation.mutate(formData)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'managerId' ? (value ? parseInt(value) : undefined) : value
    }))
  }

  const departments = [
    'Human Resources',
    'Information Technology',
    'Finance',
    'Sales',
    'Marketing',
    'Operations',
    'Legal',
    'Administration'
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-[#63d400]'
      case 'inactive': return 'text-amber-500'
      case 'terminated': return 'text-red-500'
      default: return 'text-gray-500'
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
          onClick={(e) => e.stopPropagation()}
        ></div>

        {/* Modal panel */}
        <div className="inline-block w-full max-w-2xl my-8 text-left align-middle bg-white rounded-2xl shadow-2xl transform transition-all">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-[#000065] to-[#000065]/90 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">
                  Edit Employee
                </h3>
                <p className="text-[#63d400] text-sm mt-1">
                  #{employee.employeeNumber} • {employee.firstName} {employee.lastName}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <div className="bg-white px-6 py-6 max-h-[75vh] overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information Section */}
              <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-[#000065] mb-4 flex items-center">
                  <div className="w-1 h-6 bg-[#63d400] rounded-full mr-3"></div>
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-[#000065] mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-[#000065] mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#000065] mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#000065] mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+243 81 234 5678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Job Information Section */}
              <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-[#000065] mb-4 flex items-center">
                  <div className="w-1 h-6 bg-[#63d400] rounded-full mr-3"></div>
                  Job Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="department" className="block text-sm font-medium text-[#000065] mb-2">
                      Department *
                    </label>
                    <select
                      name="department"
                      id="department"
                      required
                      value={formData.department}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    >
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="position" className="block text-sm font-medium text-[#000065] mb-2">
                      Position *
                    </label>
                    <input
                      type="text"
                      name="position"
                      id="position"
                      required
                      value={formData.position}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label htmlFor="employmentType" className="block text-sm font-medium text-[#000065] mb-2">
                      Employment Type *
                    </label>
                    <select
                      name="employmentType"
                      id="employmentType"
                      required
                      value={formData.employmentType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    >
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="employmentStatus" className="block text-sm font-medium text-[#000065] mb-2">
                      Status *
                    </label>
                    <select
                      name="employmentStatus"
                      id="employmentStatus"
                      required
                      value={formData.employmentStatus}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white ${getStatusColor(formData.employmentStatus)}`}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="terminated">Terminated</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="managerId" className="block text-sm font-medium text-[#000065] mb-2">
                    Reports To
                  </label>
                  <select
                    name="managerId"
                    id="managerId"
                    value={formData.managerId || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                  >
                    <option value="">No Manager</option>
                    {managers?.employees?.map(manager => (
                      <option key={manager.id} value={manager.id}>
                        {manager.firstName} {manager.lastName} (#{manager.employeeNumber})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Compensation Section */}
              <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-[#000065] mb-4 flex items-center">
                  <div className="w-1 h-6 bg-[#63d400] rounded-full mr-3"></div>
                  Compensation & Benefits
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label htmlFor="baseSalary" className="block text-sm font-medium text-[#000065] mb-2">
                      Base Salary *
                    </label>
                    <input
                      type="number"
                      name="baseSalary"
                      id="baseSalary"
                      required
                      min="0"
                      step="0.01"
                      value={formData.baseSalary}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="salaryCurrency" className="block text-sm font-medium text-[#000065] mb-2">
                      Currency *
                    </label>
                    <select
                      name="salaryCurrency"
                      id="salaryCurrency"
                      required
                      value={formData.salaryCurrency}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    >
                      <option value="USD">USD ($)</option>
                      <option value="CDF">CDF (FC)</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="hireDate" className="block text-sm font-medium text-[#000065] mb-2">
                      Hire Date *
                    </label>
                    <input
                      type="date"
                      name="hireDate"
                      id="hireDate"
                      required
                      value={formData.hireDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    />
                  </div>
                </div>

                {/* Allowances */}
                <div className="mt-6">
                  <h5 className="text-md font-semibold text-[#000065] mb-3">Monthly Allowances</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="transportAllowance" className="block text-sm font-medium text-[#000065] mb-2">
                        Transport Allowance
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                          {formData.salaryCurrency === 'USD' ? '$' : 'FC'}
                        </span>
                        <input
                          type="number"
                          name="transportAllowance"
                          id="transportAllowance"
                          min="0"
                          step="0.01"
                          value={formData.transportAllowance}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="telephoneAllowance" className="block text-sm font-medium text-[#000065] mb-2">
                        Telephone Allowance
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                          {formData.salaryCurrency === 'USD' ? '$' : 'FC'}
                        </span>
                        <input
                          type="number"
                          name="telephoneAllowance"
                          id="telephoneAllowance"
                          min="0"
                          step="0.01"
                          value={formData.telephoneAllowance}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="dataAllowance" className="block text-sm font-medium text-[#000065] mb-2">
                        Data Allowance
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                          {formData.salaryCurrency === 'USD' ? '$' : 'FC'}
                        </span>
                        <input
                          type="number"
                          name="dataAllowance"
                          id="dataAllowance"
                          min="0"
                          step="0.01"
                          value={formData.dataAllowance}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-gray-500">
                    Allowances are added to the base salary and are taxable
                  </p>
                </div>
              </div>

              {/* Bank Details Section */}
              <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-[#000065] mb-4 flex items-center">
                  <div className="w-1 h-6 bg-[#63d400] rounded-full mr-3"></div>
                  Bank Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="bankName" className="block text-sm font-medium text-[#000065] mb-2">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      id="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      placeholder="e.g. Equity BCDC"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="bankAccount" className="block text-sm font-medium text-[#000065] mb-2">
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="bankAccount"
                      id="bankAccount"
                      value={formData.bankAccount}
                      onChange={handleInputChange}
                      placeholder="e.g. 1234567890"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#63d400]/50 focus:border-[#63d400] transition-all bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-sm font-medium text-[#000065] bg-white hover:bg-gray-50 transition-colors shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateEmployeeMutation.isPending}
                  className="px-6 py-3 bg-[#63d400] text-white rounded-xl text-sm font-medium hover:bg-[#52b800] transition-colors shadow-lg shadow-[#63d400]/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {updateEmployeeMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    'Update Employee'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditEmployeeModal